import React, { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import type { User } from '../types';

interface CompanyUser extends User {
  testCount: number;
  lastTestDate?: Date;
  averagePerformance: {
    reactionTime: number;
    lapseRate: number;
  };
}

interface CompanyUserListProps {
  users: CompanyUser[];
  onAddUser: (userData: {
    name: string;
    email: string;
    password: string;
    role: 'ADMIN';
    companyCode?: string;
    companyName?: string;
  }) => Promise<void>;
  onEditUser: (userId: string, userData: {
    name: string;
    email: string;
    role: 'ADMIN';
  }) => Promise<void>;
  onDeleteUser: (userId: string) => Promise<void>;
  onViewHistory: (user: CompanyUser) => void;
  loading?: boolean;
  isSuperAdmin?: boolean;
}

const CompanyUserList: React.FC<CompanyUserListProps> = ({
  users,
  onAddUser,
  onEditUser,
  onDeleteUser,
  onViewHistory,
  loading = false,
  isSuperAdmin = false
}) => {
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<CompanyUser | null>(null);
  
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    email: '',
    password: '',
    companyCode: '',
    companyName: ''
  });
  
  const [editUserForm, setEditUserForm] = useState({
    name: '',
    email: ''
  });

  const [addUserError, setAddUserError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getAddUserFieldErrors = () => {
    const errors: {
      name?: string;
      email?: string;
      password?: string;
      companyCode?: string;
      companyName?: string;
    } = {};

    if (!newUserForm.name.trim()) {
      errors.name = 'Full name is required.';
    }

    if (!newUserForm.email.trim()) {
      errors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUserForm.email.trim())) {
      errors.email = 'Enter a valid email address.';
    }

    if (!newUserForm.password) {
      errors.password = 'Password is required.';
    } else if (newUserForm.password.length < 4) {
      errors.password = 'Password must be at least 4 characters.';
    }

    if (isSuperAdmin) {
      if (!newUserForm.companyCode.trim()) {
        errors.companyCode = 'Company code is required.';
      } else if (!/^\d{4}$/.test(newUserForm.companyCode.trim())) {
        errors.companyCode = 'Company code must be exactly 4 digits.';
      }
      if (!newUserForm.companyName.trim()) {
        errors.companyName = 'Company name is required.';
      }
    }

    return errors;
  };

  const addUserFieldErrors = getAddUserFieldErrors();
  const isAddUserFormValid = Object.keys(addUserFieldErrors).length === 0;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const closeAddUserModal = () => {
    setShowAddUserModal(false);
    setAddUserError(null);
  };

  const handleAddUser = async () => {
    if (!isAddUserFormValid) {
      setAddUserError('Please fix the highlighted fields before submitting.');
      return;
    }

    setAddUserError(null);
    setIsSubmitting(true);
    try {
      await onAddUser({
        name: newUserForm.name.trim(),
        email: newUserForm.email.trim(),
        password: newUserForm.password,
        role: 'ADMIN',
        companyCode: newUserForm.companyCode.trim(),
        companyName: newUserForm.companyName.trim(),
      });
      setShowAddUserModal(false);
      setNewUserForm({ name: '', email: '', password: '', companyCode: '', companyName: '' });
    } catch (error) {
      console.error('Error adding user:', error);
      const { userMessage, message } = error as { userMessage?: string; message?: string };
      setAddUserError(userMessage || message || 'Failed to add admin. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    
    try {
      await onEditUser(selectedUser.id, { ...editUserForm, role: 'ADMIN' });
      setShowEditUserModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error editing user:', error);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete admin "${selectedUser.name}"? This action cannot be undone and will delete all their test data.`
    );
    
    if (!confirmed) return;
    
    try {
      await onDeleteUser(selectedUser.id);
      setShowEditUserModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const openEditModal = (user: CompanyUser) => {
    setSelectedUser(user);
    setEditUserForm({
      name: user.name,
      email: user.email
    });
    setShowEditUserModal(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-semibold text-white">Company Admins</h3>
          <div className="h-10 w-32 bg-navy-700 animate-pulse rounded"></div>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="h-10 w-10 bg-navy-700 animate-pulse rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-navy-700 animate-pulse rounded w-1/4"></div>
                    <div className="h-3 bg-navy-700 animate-pulse rounded w-1/3"></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-white">Company Admins</h3>
          {!isSuperAdmin && (
            <p className="text-sm text-navy-300">Only a superadmin can add, edit, or remove company admins.</p>
          )}
        </div>
        {isSuperAdmin && (
          <Button 
            onClick={() => setShowAddUserModal(true)}
            className="bg-gold-500 hover:bg-gold-600"
          >
            Add New Admin
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-navy-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                    Admin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                    Tests Completed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                    Avg Reaction Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                    Lapse Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                    Last Test
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-navy-800 divide-y divide-navy-700">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-sm text-navy-300">
                      No company admins are available yet.
                    </td>
                  </tr>
                ) : users.map((user) => (
                  <tr key={user.id} className="hover:bg-navy-900">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Avatar className="h-8 w-8">
                          {user.avatar ? (
                            <AvatarImage src={user.avatar} alt={user.name} />
                          ) : (
                            <AvatarFallback className="bg-navy-800 text-navy-200 text-xs">
                              {getInitials(user.name)}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-white">{user.name}</div>
                          <div className="text-sm text-navy-300">{user.email}</div>
                          <div className="text-xs text-navy-300 capitalize">{user.role}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {user.testCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-white">
                        {Math.round(user.averagePerformance.reactionTime)}ms
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {(user.averagePerformance.lapseRate * 100).toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-navy-300">
                      {user.lastTestDate ? formatDate(user.lastTestDate) : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mr-2"
                        onClick={() => onViewHistory(user)}
                      >
                        View History
                      </Button>
                      {isSuperAdmin && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openEditModal(user)}
                        >
                          Edit
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add Admin Modal */}
      {showAddUserModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-user-title"
        >
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle id="add-user-title">Add New Admin</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={newUserForm.name}
                  onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })}
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                  placeholder="Enter email address"
                />
                {newUserForm.email && addUserFieldErrors.email && (
                  <p role="alert" className="mt-1 text-xs text-red-400">{addUserFieldErrors.email}</p>
                )}
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={newUserForm.password}
                  onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                  placeholder="Enter password"
                />
                {newUserForm.password && addUserFieldErrors.password && (
                  <p role="alert" className="mt-1 text-xs text-red-400">{addUserFieldErrors.password}</p>
                )}
              </div>
              {isSuperAdmin && (
                <>
                  <div>
                    <Label htmlFor="companyCode">Company Code (4 digits)</Label>
                    <Input
                      id="companyCode"
                      value={newUserForm.companyCode}
                      onChange={(e) => setNewUserForm({ ...newUserForm, companyCode: e.target.value })}
                      placeholder="e.g. 1001"
                    />
                    {newUserForm.companyCode && addUserFieldErrors.companyCode && (
                      <p role="alert" className="mt-1 text-xs text-red-400">{addUserFieldErrors.companyCode}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="companyName">Company Name</Label>
                    <Input
                      id="companyName"
                      value={newUserForm.companyName}
                      onChange={(e) => setNewUserForm({ ...newUserForm, companyName: e.target.value })}
                      placeholder="Enter company name"
                    />
                  </div>
                </>
              )}
              {addUserError && (
                <p role="alert" className="text-sm text-red-400">{addUserError}</p>
              )}
              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  variant="outline" 
                  onClick={closeAddUserModal}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleAddUser}
                  disabled={!isAddUserFormValid || isSubmitting}
                >
                  {isSubmitting ? 'Adding...' : 'Add Admin'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Admin Modal */}
      {showEditUserModal && selectedUser && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-user-title"
        >
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle id="edit-user-title">Edit Admin: {selectedUser.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Full Name</Label>
                <Input
                  id="edit-name"
                  value={editUserForm.name}
                  onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editUserForm.email}
                  onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                  placeholder="Enter email address"
                />
              </div>
              <div className="flex justify-between pt-4">
                <Button 
                  variant="destructive" 
                  onClick={handleDeleteUser}
                >
                  Delete Admin
                </Button>
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowEditUserModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleEditUser}
                    disabled={!editUserForm.name || !editUserForm.email}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CompanyUserList;
